import { useCallback, useEffect, useLayoutEffect, useState, useSyncExternalStore } from 'react';

/**
 * State the shell shares between its pieces (rail, top bar, tab bar, drawer), kept in one place so
 * they can never disagree: whether the rail is open, which theme is on, whether the compact
 * (phone/tablet) chrome is showing. Everything reads localStorage defensively: a private window or
 * blocked site data must leave the shell working, just without a remembered choice.
 */

/* ── Media queries ──────────────────────────────────────────────────────────────────────────── */

/** Below 1024px the shell is the platform's phone model: top pill, drawer, tab bar. */
export const COMPACT_QUERY = '(max-width: 1023px)';

function matches(query, fallback) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return fallback;
  return window.matchMedia(query).matches;
}

/** True while `query` matches. Without matchMedia (tests, very old browsers) it answers `fallback`. */
export function useMediaQuery(query, fallback = false) {
  const [value, setValue] = useState(() => matches(query, fallback));
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const list = window.matchMedia(query);
    const sync = () => setValue(list.matches);
    sync();
    if (list.addEventListener) list.addEventListener('change', sync);
    else list.addListener(sync);
    return () => {
      if (list.removeEventListener) list.removeEventListener('change', sync);
      else list.removeListener(sync);
    };
  }, [query]);
  return value;
}

/** True on phones and tablets (< 1024px): the drawer, the top pill and the tab bar are in use. */
export function useIsCompact() {
  return useMediaQuery(COMPACT_QUERY, false);
}

/* ── Rail open / closed ─────────────────────────────────────────────────────────────────────── */

const SIDEBAR_KEY = 'crm-sidebar-open';
const sidebarListeners = new Set();
let sidebarFallback = null; // carries a choice for this tab when localStorage cannot be written
let defaultAtLoad = null;

// No saved choice: the rail opens from 1440px up and stays an icon rail on 1024-1439 laptops, so
// the page column keeps its width. Decided once per page load, so resizing never flips it.
function defaultSidebarOpen() {
  if (defaultAtLoad === null) {
    if (typeof window === 'undefined') defaultAtLoad = true;
    else if (typeof window.matchMedia === 'function') defaultAtLoad = window.matchMedia('(min-width: 1440px)').matches;
    else defaultAtLoad = window.innerWidth >= 1440;
  }
  return defaultAtLoad;
}

function readSidebarOpen() {
  try {
    const saved = localStorage.getItem(SIDEBAR_KEY);
    if (saved === 'true') return true;
    if (saved === 'false') return false;
  } catch { /* storage blocked */ }
  return sidebarFallback !== null ? sidebarFallback : defaultSidebarOpen();
}

function subscribeSidebar(listener) {
  sidebarListeners.add(listener);
  window.addEventListener('storage', listener);
  return () => {
    sidebarListeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

export function setSidebarOpen(open) {
  sidebarFallback = null;
  try {
    localStorage.setItem(SIDEBAR_KEY, String(open));
  } catch {
    sidebarFallback = open;
  }
  sidebarListeners.forEach((listener) => listener());
}

/** [open, setOpen]: the desktop rail's state, shared by the rail and the top bar. */
export function useSidebarOpen() {
  const open = useSyncExternalStore(subscribeSidebar, readSidebarOpen, () => true);
  return [open, setSidebarOpen];
}

/* ── Theme ──────────────────────────────────────────────────────────────────────────────────── */

const THEME_KEY = 'crm-theme';
const themeListeners = new Set();
let themeFallback = null;

function readTheme() {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === 'dark' || saved === 'light' || saved === 'system') return saved;
  } catch { /* storage blocked */ }
  return themeFallback || 'light';
}

function subscribeTheme(listener) {
  themeListeners.add(listener);
  window.addEventListener('storage', listener);
  return () => {
    themeListeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

/** 'light' or 'dark' for a stored choice ('system' follows the device). */
export function resolveTheme(theme) {
  if (theme === 'system') return matches('(prefers-color-scheme: dark)', false) ? 'dark' : 'light';
  return theme === 'dark' ? 'dark' : 'light';
}

const THEME_COLOR = { light: '#FFFFFF', dark: '#0F0F0F' };

/**
 * Put a theme on the page: <html data-theme>, the matching `color-scheme` (native scrollbars and
 * form controls) and the browser chrome colour. The inline `color-scheme` is what the pre-paint
 * script in public/index.html sets, so it has to follow every later change too: left alone it would
 * keep the first theme's value (inline wins over the stylesheet) and a light page would draw dark
 * scrollbars after a switch from a dark boot.
 */
export function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  const resolved = resolveTheme(theme);
  const root = document.documentElement;
  root.setAttribute('data-theme', resolved);
  root.style.colorScheme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLOR[resolved]);
}

export function setTheme(next) {
  const value = next === 'dark' || next === 'system' ? next : 'light';
  themeFallback = null;
  try {
    localStorage.setItem(THEME_KEY, value);
  } catch {
    themeFallback = value;
  }
  applyTheme(value);
  themeListeners.forEach((listener) => listener());
}

// The saved theme goes on <html> as soon as this module loads, before React's first paint: the
// effect in useTheme() runs after it, and a reload in dark mode would flash light until then.
if (typeof document !== 'undefined') {
  try {
    applyTheme(readTheme());
  } catch { /* nothing to apply yet: useTheme() applies it once the shell mounts */ }
}

/** { theme, resolved, isDark, setTheme, toggle }: one theme for every control that shows it. */
export function useTheme() {
  const theme = useSyncExternalStore(subscribeTheme, readTheme, () => 'light');
  useEffect(() => {
    applyTheme(theme);
    if (theme !== 'system' || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const list = window.matchMedia('(prefers-color-scheme: dark)');
    const sync = () => applyTheme('system');
    if (list.addEventListener) list.addEventListener('change', sync);
    else list.addListener(sync);
    return () => {
      if (list.removeEventListener) list.removeEventListener('change', sync);
      else list.removeListener(sync);
    };
  }, [theme]);
  const resolved = resolveTheme(theme);
  const toggle = useCallback(() => setTheme(resolveTheme(readTheme()) === 'dark' ? 'light' : 'dark'), []);
  return { theme, resolved, isDark: resolved === 'dark', setTheme, toggle };
}

/* ── Tab bar visibility ─────────────────────────────────────────────────────────────────────── */

// A screen that pins its own control to the bottom edge (an open conversation, a long form's save
// bar) asks the tab bar to step aside with useHideBottomNav(); several screens can ask at once.
let hideCount = 0;
const hideListeners = new Set();
const emitHide = () => hideListeners.forEach((listener) => listener());

export function useHideBottomNav(active = true) {
  useEffect(() => {
    if (!active) return undefined;
    hideCount += 1;
    emitHide();
    return () => {
      hideCount -= 1;
      emitHide();
    };
  }, [active]);
}

export function useBottomNavHidden() {
  return useSyncExternalStore(
    (listener) => {
      hideListeners.add(listener);
      return () => hideListeners.delete(listener);
    },
    () => hideCount > 0,
    () => false,
  );
}

/* ── Phone chrome footprint ─────────────────────────────────────────────────────────────────── */

/**
 * Publish which piece of phone chrome is on screen as html[data-shell-top] / html[data-shell-bottom],
 * so anything a page pins to the viewport can clear it with var(--shell-top-inset) and
 * var(--shell-bottom-inset) (index.css) instead of hard-coding the bar heights.
 */
export function useShellInsets({ top, bottom }) {
  useEffect(() => {
    const root = document.documentElement;
    if (top) root.dataset.shellTop = 'on';
    else delete root.dataset.shellTop;
    if (bottom) root.dataset.shellBottom = 'on';
    else delete root.dataset.shellBottom;
    return () => {
      delete root.dataset.shellTop;
      delete root.dataset.shellBottom;
    };
  }, [top, bottom]);
}

/* ── Page column vs top bar ─────────────────────────────────────────────────────────────────── */

/**
 * The page scrolls inside .content-area, whose scrollbar takes room from the page column but not
 * from the top bar above it. This measures that room and publishes it as --content-sb on the
 * layout, so the bar gives up the same width on its right and both end on the same line (0 for
 * overlay scrollbars on touch screens). Runs before paint, so the first frame is already aligned.
 */
export function useContentGutter(layoutRef) {
  useLayoutEffect(() => {
    const layout = layoutRef.current;
    const area = layout ? layout.querySelector('.content-area') : null;
    if (!layout || !area) return undefined;
    const measure = () => {
      const width = Math.max(0, area.offsetWidth - area.clientWidth);
      layout.style.setProperty('--content-sb', `${width}px`);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    // The scrollbar appearing shrinks the area's content box, which is what the observer reports.
    const observer = new ResizeObserver(measure);
    observer.observe(area);
    return () => observer.disconnect();
  }, [layoutRef]);
}
